import type { CommandBoard } from '@atlassian/jira-command-palette-common/src/common/ui/command/board/types.tsx';
import type { CommandFilter } from '@atlassian/jira-command-palette-common/src/common/ui/command/filter/types.tsx';
import type { CommandIssue } from '@atlassian/jira-command-palette-common/src/common/ui/command/issue/types.tsx';
import type { CommandPlan } from '@atlassian/jira-command-palette-common/src/common/ui/command/plan/types.tsx';
import type { CommandProject } from '@atlassian/jira-command-palette-common/src/common/ui/command/project/types.tsx';
import type {
	QuickSearchIssueResult,
	QuickSearchIssueScope,
	QuickSearchProjectResult,
	QuickSearchProjectScope,
	QuickSearchResponseData,
	QuickSearchBoardScope,
	QuickSearchBoardResult,
	QuickSearchFilterScope,
	QuickSearchFilterResult,
	QuickSearchPlanScope,
	QuickSearchPlanResult,
} from '../types.tsx';

export const transformQuickSearchIssueResultToIssue = (
	issue: QuickSearchIssueResult,
): CommandIssue => ({
	issueId: issue.id,
	summary: issue.name,
	url: issue.url,
	issueKey: issue.attributes.key,
	avatar: issue.attributes.avatar,
	projectName: issue.attributes.container.title,
	projectId: issue.attributes.containerId,
	issueTypeName: issue.attributes.issueTypeName,
});

export const getQuickSearchIssueScope = (
	results?: QuickSearchResponseData,
): QuickSearchIssueScope | undefined => {
	const issueResults = results?.scopes?.find((scope) => scope.id === 'jira.issue');
	if (issueResults) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return issueResults as QuickSearchIssueScope;
	}
	return undefined;
};

export const transformQuickSearchBoardResultToBoard = (
	board: QuickSearchBoardResult,
): CommandBoard => ({
	id: board.id,
	url: board.url,
	name: board.name,
	containerId: board.attributes.containerId,
	containerName: board.attributes.containerName,
	isFavourite: board.attributes.isFavourite,
});

export const getQuickSearchBoardScope = (
	results?: QuickSearchResponseData,
): QuickSearchBoardScope | undefined => {
	const boardResults = results?.scopes?.find((scope) => scope.id === 'jira.board');
	if (boardResults) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return boardResults as QuickSearchBoardScope;
	}
	return undefined;
};

export const getQuickSearchProjectScope = (
	results?: QuickSearchResponseData,
): QuickSearchProjectScope | undefined => {
	const issueResults = results?.scopes?.find((scope) => scope.id === 'jira.project');
	if (issueResults) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return issueResults as QuickSearchProjectScope;
	}
	return undefined;
};

export const transformQuickSearchProjectResultToProject = (
	project: QuickSearchProjectResult,
): CommandProject => ({
	projectId: project.id,
	name: project.name,
	url: project.url,
	avatar: project.attributes.avatar,
	canView: project.attributes.canView,
	isFavourite: project.attributes.isFavourite,
	projectKey: project.attributes.projectKey,
	projectType: project.attributes.projectType,
	simplified: project.attributes.simplified,
});

export const getQuickSearchFilterScope = (
	results?: QuickSearchResponseData,
): QuickSearchFilterScope | undefined => {
	const filterResults = results?.scopes?.find((scope) => scope.id === 'jira.filter');
	if (filterResults) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return filterResults as QuickSearchFilterScope;
	}
	return undefined;
};

export const transformQuickSearchFilterResultToFilter = (
	filter: QuickSearchFilterResult,
): CommandFilter => ({
	id: filter.id,
	name: filter.name,
	url: filter.url,
});

export const getQuickSearchPlanScope = (
	results?: QuickSearchResponseData,
): QuickSearchPlanScope | undefined => {
	const planResults = results?.scopes?.find((scope) => scope.id === 'jira.plan');
	if (planResults) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return planResults as QuickSearchPlanScope;
	}
	return undefined;
};

export const transformQuickSearchPlanResultToPlan = (plan: QuickSearchPlanResult): CommandPlan => ({
	id: plan.id,
	name: plan.name,
	url: plan.url,
});
